import { Box, styled } from "@mui/material";
import React from "react";
import type { UIEvent } from "react";

import type { ExtendedChatMessage } from "../hooks/useAssistantChat";
import MessageComponent from "./Message";

const MessagesContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isCompactMode",
})<{ isCompactMode?: boolean }>(({ theme, isCompactMode }) => ({
  flex: 1,
  overflowY: "auto",
  padding: isCompactMode ? theme.spacing(1) : theme.spacing(2),
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2),
  scrollBehavior: "smooth",
  overscrollBehavior: "contain",
}));

interface MessageListProps {
  messages: ExtendedChatMessage[];
  isLoading: boolean;
  messagesEndRef: React.RefObject<HTMLDivElement>;
  messagesContainerRef: React.RefObject<HTMLDivElement>;
  handleScroll: (e: UIEvent<HTMLDivElement>) => void;
  retryLastRequest: () => void;
  isCompactMode?: boolean;
  role?: string;
}

const MessageList: React.FC<MessageListProps> = ({
  messages,
  isLoading,
  messagesEndRef,
  messagesContainerRef,
  handleScroll,
  retryLastRequest,
  isCompactMode = false,
  role,
}) => {
  return (
    <MessagesContainer
      isCompactMode={isCompactMode}
      ref={messagesContainerRef}
      onScroll={handleScroll}
      role={role}
    >
      {messages.map((msg, index) => {
        const isTypingMessage =
          isLoading && index === messages.length - 1 && !msg.isUser;

        const lastUserMessageIndex = [...messages]
          .reverse()
          .findIndex((m) => m.isUser);

        const lastUserMessagePosition =
          lastUserMessageIndex !== -1
            ? messages.length - 1 - lastUserMessageIndex
            : -1;

        const showRetry = msg.isUser && index === lastUserMessagePosition;

        return (
          <MessageComponent
            key={index}
            text={msg.text}
            isUser={msg.isUser}
            timestamp={msg.timestamp}
            isTyping={isTypingMessage}
            onRetry={showRetry ? retryLastRequest : undefined}
          />
        );
      })}
      <div ref={messagesEndRef} />
    </MessagesContainer>
  );
};

export default MessageList;
